/**
 * Доля ядра публичного рендерера: вид `stripe_wallets_express` (кнопки
 * Apple Pay / Google Pay).
 *
 * WEB-2921, заход 1: компонент `WalletsExpressButtons` перенесён из
 * `PublicBlockRenderer.tsx` слово в слово (у кнопок свои хуки — оплата,
 * ответы формы, состояние нажатия, поэтому это компонент). Метка
 * `case 'stripe_wallets_express'` и вызов компонента остаются в ядре. Это
 * денежная дверь: цену считает та же лестница `resolvePriceId`, что у
 * BuyNowButton. Доля едет в первом экране вместе с ядром (статический импорт).
 */
import React, { useState } from 'react';
import type { EditorBlock } from '@/store/editorSlice';
import { useFormValues } from '../FormValuesContext';
import { usePaywallCheckout } from '../paywallCheckoutContext';
import { useCheckoutUnavailable } from '@/components/paywall-runtime/checkoutAvailability';
import { checkoutErrorText } from '@/components/paywall-runtime/checkoutErrorText';
import { resolvePriceId } from '@/components/paywall-runtime/resolvePriceId';

/**
 * WEB-704 (вариант A, ратиф. PM): express-wallet кнопки (Apple/Google Pay)
 * запускают существующий hosted Stripe Checkout по цене блока — тот же
 * money-путь, что «Buy Now»/downsell CTA (`PaywallCheckoutProvider`). Вне
 * пейволла (квиз/editor-превью) контекст `null` → кнопки инертны.
 */
const WALLET_LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay', // i18n-allow: название платёжного бренда, не переводится
  google_pay: 'Google Pay', // i18n-allow: название платёжного бренда, не переводится
};

export function WalletsExpressButtons({
  block,
  locale,
  screenBlocks,
  funnelBlocks,
  hiddenBlockIds,
}: {
  block: EditorBlock;
  /** WEB-967: язык воронки для текстов отказа оплаты. */
  locale?: string | null;
  /**
   * WEB-1302: блоки экрана и воронки — те же входы resolvePriceId, что и у
   * обычной кнопки оплаты (BuyNowButton). Без них кошелёк платил бы своей
   * ценой (block.stripePriceId) вместо выбранного покупателем тарифа.
   */
  screenBlocks?: EditorBlock[];
  funnelBlocks?: EditorBlock[];
  /**
   * WEB-1113 доводка (проверка пачек 23.08, FC3a): id блоков экрана, скрытых
   * правилом видимости — второй заслон внутри resolvePriceId (см. её
   * doc-комментарий). Раньше не пробрасывался сюда вовсе.
   */
  hiddenBlockIds?: Set<string>;
}): React.ReactElement | null {
  const startCheckout = usePaywallCheckout();
  const { values } = useFormValues();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // WEB-2009 (хвост WEB-1989 шаг 2): показ пришёл из копии на краю — живого
  // API нет, чекаут не пройдёт. Кошелёк — та же денежная дверь, что
  // BuyNowButton, и обязана слушать тот же признак (checkoutAvailability.ts);
  // до этой правки кнопка оставалась нажимаемой при stale.
  const checkoutUnavailable = useCheckoutUnavailable();
  const wallets = block.wallets ?? [];
  if (wallets.length === 0) return null;

  // WEB-1302: тот же резолвер, которым уже пользуется BuyNowButton — сперва
  // живой выбор «Выбора тарифа» (в т.ч. с предыдущего экрана), иначе
  // собственная цена блока / соседнего price_block на этом же экране.
  // Раньше здесь безусловно бралось block.stripePriceId — кошелёк списывал
  // свою цену, даже когда покупатель выбрал другой тариф.
  //
  // WEB-1714 п.6: считается на отрисовке и уходит И в оплату, И в атрибут
  // `data-stripe-price` ниже. Атрибут печатал свою цену блока — ручная
  // проверка через инспектор врала ровно там, где деньги (вторая дверь той же
  // дыры, первая — BuyNowButton).
  //
  // ⚠ WEB-1799 п.3 — ГРАНИЦА ЭТОГО УТВЕРЖДЕНИЯ, названная вслух. Атрибут
  // `data-stripe-price` печатает СПИСЫВАЕМУЮ цену только у этих двух дверей
  // (кошелёк здесь и BuyNowButton). WEB-1860 закрыл вторую половину границы:
  // в ветках `case 'stripe_checkout_form'` и `case 'button_to_pay'` этого же
  // файла атрибут печатал собственную цену блока — теперь его там НЕТ вовсе.
  // Это мёртвая разметка: на платёжном пути обе ветки недостижимы
  // (PaywallScreenWizard перехватывает оба типа ВЫШЕ и рисует вместо них
  // BuyNowButton), обработчика нажатия у самих кнопок нет, а отрисоваться они
  // могут (экран КВИЗА через QuizScreenWizard и превью редактора) — то есть
  // атрибут врал глазу проверяющего, хоть и не кошельку покупателя. Пересчёт
  // через resolvePriceId там невозможен: платёжного контекста нет.
  //
  // ⚠ ТРЕТИЙ ИСТОЧНИК АТРИБУТА, названный ревизией пачки 3 (находка 7):
  // PublicBlockRendererExtended.tsx, карточка «Выбора тарифа» — там
  // `data-stripe-price` вешается на ВЫБРАННУЮ карточку (`plan.priceId`, и
  // только при isSelected). Роль у него другая: это не кнопка и не оплата, а
  // МЕТКА живого выбора — по ней проверяющий и наборы находят, какой тариф
  // сейчас выбран. Врать деньгам она не может, но и не «читается» оплатой:
  // resolvePriceId берёт выбор не из разметки, а из значений формы
  // (`values[идентификатор селектора]`) — то есть оба конца независимо
  // выводятся из одного живого выбора, а не один из другого. Итого мест, где
  // печатается этот атрибут, ТРИ (после WEB-1860): две денежные двери
  // (кошелёк здесь и BuyNowButton) и метка выбранного тарифа.
  const chargedPriceId = resolvePriceId(
    block,
    screenBlocks ?? [],
    values,
    funnelBlocks,
    hiddenBlockIds,
  );

  const handleClick = async () => {
    if (!startCheckout || isLoading || checkoutUnavailable) return;
    setIsLoading(true);
    setError(null);
    const priceId = chargedPriceId;
    const result = await startCheckout(priceId, {
      fireDownsellOnFailure: true,
    });
    // WEB-933 п.2: единый текст покупателю (checkoutErrorText).
    setError(checkoutErrorText(result, locale));
    // On success the runner already redirected to Stripe (navigating away).
    setIsLoading(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 16,
      }}
    >
      {error && (
        <div
          data-testid="wallet-express-error"
          style={{ color: 'var(--color-error, #ff4d4f)', fontSize: 13 }}
        >
          {error}
        </div>
      )}
      {wallets.map((w) => (
        <button
          key={w}
          onClick={handleClick}
          disabled={isLoading || checkoutUnavailable}
          data-testid={`wallet-express-${w}`}
          data-stripe-price={chargedPriceId}
          data-wallet={w}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: 'var(--color-text-primary, #000)',
            color: 'var(--color-bg-surface, #fff)',
            border: 'none',
            borderRadius: 'var(--border-radius-button, 8px)',
            fontSize: 15,
            fontWeight: 600,
            cursor: isLoading || checkoutUnavailable ? 'not-allowed' : 'pointer',
            opacity: isLoading || checkoutUnavailable ? 0.7 : 1,
          }}
        >
          {WALLET_LABELS[w] ?? w}
        </button>
      ))}
    </div>
  );
}
